import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  ViewChild,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import { CalendarIcon } from 'primeng/icons/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { isActivationKey } from '../../../utilities/accessibility';
import {
  DATEPICKER_PANEL_SELECTOR,
  DATEPICKER_WEEKDAY_NAMES,
  enhanceDatepickerDayCells,
  observeDatepickerDayCells,
} from '../../../utilities/datepicker-day-a11y';
import {
  cleanupOverlaysOnNavigation,
  removeOrphanedOverlays,
} from '../../../utilities/overlay-cleanup';
import { BaseControlComponent } from './base-control.component';
import { FormFieldComponent } from './form-field.component';
@Component({
  selector: 'app-date-picker',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    InputTextModule,
    FormFieldComponent,
    DatePickerModule,
    CalendarIcon,
  ],
  template: `
    <app-form-field
      [control]="control"
      [id]="id"
      [label]="label"
      [helpText]="helpText"
      [showHelpText]="showHelpText"
      [tooltip]="tooltip"
      [errorId]="errorId"
      [placeholder]="placeholder"
      [screenReaderLabel]="screenReaderLabel"
      [screenReaderLabelPrefix]="screenReaderLabelPrefix"
    >
      <p-datepicker
        #datepicker
        [formControl]="control"
        [iconDisplay]="'input'"
        [showIcon]="true"
        [inputId]="id"
        appendTo="body"
        [showClear]="showClear"
        [minDate]="minDate"
        [maxDate]="maxDate"
        [selectOtherMonths]="true"
        [inputStyleClass]="
          'tw-w-full app-input-border' +
          (touchedInvalid ? ' ng-invalid ng-dirty' : '')
        "
        class="tw-w-full"
        [class]="{
          'ng-invalid': touchedInvalid,
          'ng-dirty': touchedInvalid,
        }"
        hourFormat="12"
        [showTime]="showTime || timeOnly"
        [timeOnly]="timeOnly"
        [ariaLabelledBy]="
          label || placeholder || screenReaderLabel || screenReaderLabelPrefix
            ? id + '-label'
            : null
        "
        [required]="required"
        [showOnFocus]="false"
        (onShow)="onPanelShow()"
        (onClose)="onPanelHide()"
      >
        <ng-template
          #inputicon
          pTemplate="inputicon"
          let-clickCallBack="clickCallBack"
        >
          <button
            type="button"
            class="p-datepicker-input-icon tw-appearance-none tw-border-0 tw-bg-transparent tw-p-0 tw-leading-none tw-cursor-pointer tw-inline-flex tw-items-center tw-justify-center tw-rounded-sm focus:tw-outline focus:tw-outline-2 focus:tw-outline-offset-[3px]"
            [attr.aria-label]="calendarIconAriaLabel"
            aria-haspopup="dialog"
            [attr.aria-expanded]="datepickerRef?.overlayVisible ?? false"
            [disabled]="disabled"
            (click)="clickCallBack($event)"
          >
            <svg
              data-p-icon="calendar"
              class="p-datepicker-input-icon"
              aria-hidden="true"
            ></svg>
          </button>
        </ng-template>
      </p-datepicker>
    </app-form-field>
  `,
})
export class DatePickerComponent
  extends BaseControlComponent
  implements AfterViewInit, AfterViewChecked
{
  private zone = inject(NgZone);
  private el = inject(ElementRef);
  private router = inject(Router);

  /** Selector for the PrimeNG datepicker overlay this component owns. */
  private static readonly OVERLAY_SELECTOR = DATEPICKER_PANEL_SELECTOR;

  @ViewChild('dpInput', { read: ElementRef })
  dpInput?: ElementRef<HTMLInputElement>;

  @ViewChild('datepicker')
  datepickerRef?: DatePicker;

  @Input()
  showClear = false;

  @Input()
  showTime = false;

  /** Time spinner only. The calendar grid stays hidden. */
  @Input()
  timeOnly = false;

  @Input()
  minDate?: Date;

  @Input()
  maxDate?: Date;

  /**
   * If false, calendar only opens with Enter/Space/Click.
   * If true, calendar also opens on focus, except when focus comes from Tab.
   */
  @Input()
  showOnFocus = true;

  /** Screen reader only label when no visible label is shown */
  @Input()
  screenReaderLabel: string;

  /**
   * Prepended to accessible name only; forwarded to form field (not visible).
   */
  @Input()
  screenReaderLabelPrefix: string;

  get calendarIconAriaLabel(): string {
    const field = [
      this.screenReaderLabelPrefix,
      this.label || this.screenReaderLabel || this.placeholder,
    ]
      .filter(Boolean)
      .join(' ');
    if (this.timeOnly) {
      return field ? `Open ${field} picker` : 'Open time picker';
    }
    return field ? `Open ${field} calendar` : 'Open calendar';
  }

  private lastFocusedElement: HTMLElement | null = null;
  private inputElement: HTMLInputElement | null = null;
  private currentPanel: HTMLElement | null = null;
  private static tabFocusPending = false;
  private static tabTrackingAttached = false;

  private boundKeydownHandler = this.handleCalendarKeydown.bind(this);
  private boundFocusTrapHandler = this.handleFocusTrap.bind(this);
  private boundInputFocusHandler = this.handleInputFocus.bind(this);
  private boundInputClickHandler = this.handleInputClick.bind(this);
  private boundDocumentActivationHandler =
    this.handleDocumentActivation.bind(this);
  private inputListenersAttached = false;
  private documentActivationListenerAttached = false;
  private isOurPanelOpen = false;
  private suppressNextFocusOpen = false;
  private panelContentObserver: MutationObserver | null = null;

  private static handleDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      DatePickerComponent.tabFocusPending = true;
    }
  }

  private static handleDocumentPointerdown(): void {
    DatePickerComponent.tabFocusPending = false;
  }

  ngAfterViewInit(): void {
    this.setDescribedBy();
    this.attachDocumentActivationListener();

    if (!DatePickerComponent.tabTrackingAttached) {
      DatePickerComponent.tabTrackingAttached = true;
      document.addEventListener(
        'keydown',
        DatePickerComponent.handleDocumentKeydown,
        true,
      );
      document.addEventListener(
        'pointerdown',
        DatePickerComponent.handleDocumentPointerdown,
        true,
      );
    }

    /*
     * WORKAROUND: PrimeNG datepicker panels appended to <body> can be left
     * behind in the DOM when the host is destroyed mid-animation (e.g.
     * router navigation while the calendar is open on a mobile device).
     * Hide the panel on NavigationStart and sweep any stragglers.
     */
    this.dispose.subs = cleanupOverlaysOnNavigation(this.router, () =>
      this.cleanupOverlays(),
    );
  }

  ngAfterViewChecked(): void {
    this.setDescribedBy();
  }

  private setDescribedBy(): void {
    const input = document.getElementById(this.id) as HTMLInputElement | null;
    if (input) {
      if (this.describedBy) {
        input.setAttribute('aria-describedby', this.describedBy);
      } else {
        input.removeAttribute('aria-describedby');
      }
    }

    this.attachInputListeners();
  }

  private getInputElement(): HTMLInputElement | null {
    return (
      (this.datepickerRef?.inputfieldViewChild?.nativeElement as
        | HTMLInputElement
        | undefined) ??
      (document.getElementById(this.id) as HTMLInputElement | null)
    );
  }

  private attachInputListeners(): void {
    const input = this.getInputElement();
    if (!input || this.inputListenersAttached) {
      return;
    }

    this.inputElement = input;
    this.inputListenersAttached = true;
    input.addEventListener('focus', this.boundInputFocusHandler);
    input.addEventListener('click', this.boundInputClickHandler);
  }

  private attachDocumentActivationListener(): void {
    if (this.documentActivationListenerAttached) {
      return;
    }

    this.documentActivationListenerAttached = true;
    document.addEventListener(
      'keydown',
      this.boundDocumentActivationHandler,
      true,
    );
  }

  private handleDocumentActivation(event: KeyboardEvent): void {
    if (!isActivationKey(event)) {
      return;
    }

    const input = this.getInputElement();
    if (
      !input ||
      document.activeElement !== input ||
      !this.el.nativeElement.contains(input)
    ) {
      return;
    }

    if (this.datepickerRef?.overlayVisible) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    DatePickerComponent.tabFocusPending = false;
    this.openOverlay();
  }

  private openOverlay(): void {
    if (this.datepickerRef?.overlayVisible) {
      return;
    }

    const iconButton = this.el.nativeElement.querySelector(
      'button[aria-haspopup="dialog"]',
    ) as HTMLButtonElement | null;
    if (iconButton) {
      iconButton.click();
      return;
    }

    this.datepickerRef?.showOverlay();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cleanup();
    this.cleanupOverlays();
  }

  /**
   * Hide PrimeNG calendar overlay and remove any DOM remnants.
   * Safe to call multiple times.
   */
  private cleanupOverlays(): void {
    if (this.datepickerRef?.overlayVisible) {
      this.datepickerRef.hideOverlay();
    }
    removeOrphanedOverlays(DatePickerComponent.OVERLAY_SELECTOR);
  }

  onPanelShow(): void {
    this.isOurPanelOpen = true;
    this.lastFocusedElement = this.getInputElement();

    // Wait for panel to be fully rendered in the DOM
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Access the panel through PrimeNG's overlay property
          const panel = this.datepickerRef?.overlayVisible
            ? document.querySelector('.p-datepicker-panel:last-of-type')
            : null;

          if (panel instanceof HTMLElement) {
            this.currentPanel = panel;
            this.setupPanel(panel);
          }
        });
      });
    });
  }

  onPanelHide(): void {
    if (this.isOurPanelOpen) {
      this.cleanupPanel();
      this.isOurPanelOpen = false;

      if (this.lastFocusedElement) {
        this.suppressNextFocusOpen = true;
        this.lastFocusedElement.focus();
        this.lastFocusedElement = null;
      }
    }
  }

  private setupPanel(panel: HTMLElement): void {
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute(
      'aria-label',
      this.timeOnly ? 'Choose time' : 'Choose date',
    );

    panel.addEventListener('keydown', this.boundKeydownHandler);
    panel.addEventListener('keydown', this.boundFocusTrapHandler);

    if (!this.timeOnly) {
      this.setupCalendarNavigation(panel);
    }
    this.enhanceTimePickerButtonLabels(panel);

    this.setupPanelContentObserver(panel);

    if (this.timeOnly) {
      this.focusInitialTime(panel);
    } else {
      this.focusInitialDay(panel);
    }
  }

  private setupPanelContentObserver(panel: HTMLElement): void {
    this.panelContentObserver?.disconnect();
    this.panelContentObserver = observeDatepickerDayCells(panel, {
      onStructureChange: (p) => {
        if (!this.timeOnly) {
          this.setupCalendarNavigation(p);
          this.focusInitialDay(p);
        }
        this.enhanceTimePickerButtonLabels(p);
      },
    });
  }

  private focusInitialDay(panel: HTMLElement): void {
    const focusTarget =
      panel.querySelector<HTMLElement>(
        'td[data-p-selected="true"] .p-datepicker-day',
      ) ||
      panel.querySelector<HTMLElement>(
        '.p-datepicker-today .p-datepicker-day',
      ) ||
      panel.querySelector<HTMLElement>(
        '.p-datepicker-day:not([data-p-disabled="true"])',
      );

    if (focusTarget) {
      focusTarget.setAttribute('tabindex', '0');
      focusTarget.focus();
    }
  }

  private focusInitialTime(panel: HTMLElement): void {
    const active = document.activeElement;
    if (active instanceof HTMLElement && panel.contains(active)) {
      return;
    }
    panel
      .querySelector<HTMLElement>('.p-datepicker-hour-picker button')
      ?.focus();
  }

  private setupCalendarNavigation(panel: HTMLElement): void {
    const monthYearText = this.getCurrentMonthYear(panel);

    // aria-live on the header so month/year changes are announced
    const header = panel.querySelector('.p-datepicker-header');
    if (header) {
      header.setAttribute('aria-live', 'polite');
    }

    // Table as grid with descriptive label
    const table = panel.querySelector('table');
    if (table) {
      table.setAttribute('role', 'grid');
      table.setAttribute(
        'aria-label',
        monthYearText ? `Calendar, ${monthYearText}` : 'Calendar',
      );
    }

    // Header cells: scope="col", hide abbreviation from SR, add sr-only full name
    const headerCells = panel.querySelectorAll('table thead th');
    headerCells.forEach((th, i) => {
      th.setAttribute('scope', 'col');
      if (
        i < DATEPICKER_WEEKDAY_NAMES.length &&
        !th.querySelector('.tw-sr-only')
      ) {
        // Wrap existing abbreviated text so SR skips it
        Array.from(th.childNodes).forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            (node as HTMLElement).setAttribute('aria-hidden', 'true');
          } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
            const wrapper = document.createElement('span');
            wrapper.setAttribute('aria-hidden', 'true');
            th.replaceChild(wrapper, node);
            wrapper.appendChild(node);
          }
        });

        const srSpan = document.createElement('span');
        srSpan.className = 'tw-sr-only';
        srSpan.textContent = DATEPICKER_WEEKDAY_NAMES[i];
        th.appendChild(srSpan);
      }
    });

    enhanceDatepickerDayCells(panel);

    this.enhanceMonthYearPickerSemantics(panel);
    this.enhanceMonthYearButtonLabels(panel);
  }

  /**
   * PrimeNG sets static aria-label ("Choose Month") that overrides visible text ("July").
   * WCAG requires the accessible name to include the visible label.
   */
  private enhanceMonthYearButtonLabels(panel: HTMLElement): void {
    for (const [selector, fallbackAction] of [
      ['.p-datepicker-select-month, [data-pc-section="monthtitle"]', 'Choose Month'],
      ['.p-datepicker-select-year, [data-pc-section="yeartitle"]', 'Choose Year'],
    ] as const) {
      const button = panel.querySelector<HTMLElement>(selector);
      if (!button) {
        continue;
      }

      const visibleText = button.textContent?.trim() || '';
      if (!visibleText) {
        continue;
      }

      const existingLabel = button.getAttribute('aria-label') ?? fallbackAction;
      const action = existingLabel.startsWith(`${visibleText}, `)
        ? existingLabel.slice(`${visibleText}, `.length)
        : existingLabel;

      button.setAttribute('aria-label', `${visibleText}, ${action}`);
    }
  }

  /** PrimeNG month/year cells are plain spans; expose button role for SR without CSS changes. */
  private enhanceMonthYearPickerSemantics(panel: HTMLElement): void {
    panel.querySelectorAll('.p-datepicker-month-view > span').forEach((el) => {
      el.setAttribute('role', 'button');
    });
    panel.querySelectorAll('.p-datepicker-year-view > span').forEach((el) => {
      el.setAttribute('role', 'button');
    });
  }

  /** PrimeNG sets aria-label on p-button host, not the focusable inner button. */
  private enhanceTimePickerButtonLabels(panel: HTMLElement): void {
    if (!this.showTime && !this.timeOnly) {
      return;
    }

    for (const [selector, up, down] of [
      ['.p-datepicker-hour-picker', 'Increase hours', 'Decrease hours'],
      ['.p-datepicker-minute-picker', 'Increase minutes', 'Decrease minutes'],
      ['.p-datepicker-second-picker', 'Increase seconds', 'Decrease seconds'],
      ['.p-datepicker-ampm-picker', 'AM Button', 'PM Button'],
    ] as const) {
      panel
        .querySelector(selector)
        ?.querySelectorAll<HTMLButtonElement>('button')
        .forEach((btn) => {
          if (btn.classList.contains('p-datepicker-increment-button')) {
            btn.setAttribute('aria-label', up);
          } else if (btn.classList.contains('p-datepicker-decrement-button')) {
            btn.setAttribute('aria-label', down);
          }
        });
    }
  }

  private getCurrentMonthYear(panel: HTMLElement): string {
    // Try to get month and year from the header buttons
    const monthButton = panel.querySelector<HTMLElement>(
      '.p-datepicker-select-month, [data-pc-section="monthtitle"], button[aria-label*="month" i]',
    );
    const yearButton = panel.querySelector<HTMLElement>(
      '.p-datepicker-select-year, [data-pc-section="yeartitle"], button[aria-label*="year" i]',
    );

    const month = monthButton?.textContent?.trim() || '';
    const year = yearButton?.textContent?.trim() || '';

    if (month && year) {
      return `${month} ${year}`;
    }

    // Fallback: try to find the title element
    const title = panel.querySelector<HTMLElement>(
      '.p-datepicker-title, .p-datepicker-header',
    );
    if (title) {
      return title.textContent?.trim() || '';
    }

    return '';
  }

  private handleCalendarKeydown(event: KeyboardEvent): void {
    if (!this.isOurPanelOpen) {
      return;
    }

    const target = event.target as HTMLElement;

    // Only handle keyboard events on day cells
    if (!target.classList.contains('p-datepicker-day')) {
      return;
    }

    const currentCell = target.closest('td');
    if (!currentCell || !this.currentPanel) {
      return;
    }

    let nextCell: HTMLElement | null;

    switch (event.key) {
      case 'ArrowLeft':
        nextCell = this.getAdjacentDay(currentCell, -1);
        break;
      case 'ArrowRight':
        nextCell = this.getAdjacentDay(currentCell, 1);
        break;
      case 'ArrowUp':
        nextCell = this.getAdjacentDay(currentCell, -7);
        break;
      case 'ArrowDown':
        nextCell = this.getAdjacentDay(currentCell, 7);
        break;
      case 'Enter':
      case ' ':
        target.click();
        event.preventDefault();
        return;
      case 'Escape':
        if (this.datepickerRef?.overlayVisible) {
          this.datepickerRef.hideOverlay();
        }
        event.preventDefault();
        return;
      default:
        return;
    }

    if (nextCell) {
      event.preventDefault();
      const dayButton =
        nextCell.querySelector<HTMLElement>('.p-datepicker-day');
      if (dayButton && !nextCell.hasAttribute('data-p-disabled')) {
        target.setAttribute('tabindex', '-1');
        dayButton.setAttribute('tabindex', '0');
        dayButton.focus();
      }
    }
  }

  private handleFocusTrap(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || !this.currentPanel) {
      return;
    }

    const focusable = Array.from(
      this.currentPanel.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [tabindex="0"], input:not([disabled]), select:not([disabled]), a[href]',
      ),
    ).filter((el) => el.offsetParent !== null);

    if (focusable.length === 0) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private getAdjacentDay(
    currentCell: Element,
    offset: number,
  ): HTMLTableCellElement | null {
    if (!this.currentPanel) {
      return null;
    }

    // Get all day cells in the calendar
    const allCells = Array.from(
      this.currentPanel.querySelectorAll<HTMLTableCellElement>(
        'td:has(.p-datepicker-day)',
      ),
    );

    const currentIndex = allCells.indexOf(currentCell as HTMLTableCellElement);
    if (currentIndex === -1) {
      return null;
    }

    const nextIndex = currentIndex + offset;
    if (nextIndex >= 0 && nextIndex < allCells.length) {
      const nextCell = allCells[nextIndex];
      // Check if the cell is not disabled
      if (!nextCell.hasAttribute('data-p-disabled')) {
        return nextCell;
      }
    }

    return null;
  }

  private cleanupPanel(): void {
    this.panelContentObserver?.disconnect();
    this.panelContentObserver = null;

    if (this.currentPanel) {
      this.currentPanel.removeEventListener(
        'keydown',
        this.boundKeydownHandler,
      );
      this.currentPanel.removeEventListener(
        'keydown',
        this.boundFocusTrapHandler,
      );
      this.currentPanel = null;
    }
  }

  private handleInputFocus(): void {
    if (this.suppressNextFocusOpen) {
      this.suppressNextFocusOpen = false;
      return;
    }

    if (DatePickerComponent.tabFocusPending) {
      DatePickerComponent.tabFocusPending = false;
      return;
    }

    if (this.showOnFocus) {
      this.openOverlay();
    }
  }

  private handleInputClick(): void {
    this.openOverlay();
  }

  private cleanup(): void {
    this.cleanupPanel();

    if (this.documentActivationListenerAttached) {
      document.removeEventListener(
        'keydown',
        this.boundDocumentActivationHandler,
        true,
      );
      this.documentActivationListenerAttached = false;
    }

    if (this.inputElement) {
      this.inputElement.removeEventListener(
        'focus',
        this.boundInputFocusHandler,
      );
      this.inputElement.removeEventListener(
        'click',
        this.boundInputClickHandler,
      );
      this.inputElement = null;
    }

    this.inputListenersAttached = false;
    this.suppressNextFocusOpen = false;
    this.lastFocusedElement = null;
    this.isOurPanelOpen = false;
  }
}
